import { createTheme, type Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { appBarEnvironmentColor } from '../config/appEnvironment';

// Microsoft Fluent-inspired palette.
const fluentBlue = '#0f6cbd';

/**
 * Builds the MUI theme for the employee portal in the given color mode.
 *
 * @param mode - `'light'` or `'dark'`.
 * @returns A configured MUI {@link Theme} using the Fluent-inspired palette
 *   and component style overrides.
 */
export function buildTheme(mode: 'light' | 'dark'): Theme {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: fluentBlue,
      },
      secondary: {
        main: '#5c2e91',
      },
      background:
        mode === 'light'
          ? { default: '#f5f5f5', paper: '#ffffff' }
          : { default: '#1b1a19', paper: '#252423' },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily:
        '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(128,128,128,0.2)',
            backgroundColor: appBarEnvironmentColor(mode),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiDataGrid: {
        styleOverrides: {
          columnHeader: {
            display: 'flex',
            alignItems: 'center',
          },
          columnHeaderTitleContainer: {
            alignItems: 'center',
          },
          columnHeaderTitle: {
            fontWeight: 600,
            lineHeight: 1.3,
          },
          cell: {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'normal',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            verticalAlign: 'middle',
          },
          head: {
            fontWeight: 600,
          },
        },
      },
    },
  });
}
